import React, { useState, useEffect } from "react";
import { Box, useColorMode, useBreakpointValue } from "@chakra-ui/react";
import { useParams, useHistory } from "react-router-dom";
import ItineraryDetails from "../Itinerary/ItineraryDetails";
import ActivitiesList from "../Itinerary/ActivitiesList";
import HotelsList from "../Itinerary/HotelsList";
import FlightsList from "../Itinerary/FlightsList";
import AllActivitiesList from "../Itinerary/AllActivitiesList";
import RVsList from "../Itinerary/RVsList";
import GoogleMapsLink from "../Itinerary/MapEmbed";
// import NoOngoingTrip from "../NoOngoingTrip";
import ItineraryHeader from "./ItineraryHeader";
import axiosInstance from "../../helpers/axiosInstance"; // Make sure to import your axios instance

import { useCurrency } from "../CurrencyExchange/CurrencyContext";
import { useTranslation } from "../LanguageTranslation/TranslationContext";

import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import officialLogo from "../../assets/images/official-logo.png";
import qrCode from "../../assets/images/qr-code.png";
import countries from "i18n-iso-countries";
import ManageItinerary from "../common/ManageItinerary";
import BusTicketList from "../Itinerary/BusTicketList";
import RentalCarList from "../Itinerary/RentalCarList";
import TrainTicketList from "../Itinerary/TrainTicketList";
import OtherList from "../Itinerary/OtherList";

const ItineraryDetail = () => {
  const { colorMode } = useColorMode();
  const history = useHistory();
  const { tripId } = useParams(); // Extract the trip ID from the route parameters
  const { translateText } = useTranslation();
  const [selectedLocation, setSelectedLocation] = useState({
    latitude: null,
    longitude: null,
  });
  const [itinerary, setItinerary] = useState(null);
  const [allowedUsers, setAllowedUsers] = useState([]);
  const [activities, setActivities] = useState([]); // Used for AllActivitiesList
  const [activitiesLocation, setActivitiesLocation] = useState([]);
  const [selectedCityIndex, setSelectedCityIndex] = useState(0);
  const [selectedDayIndex, setSelectedDayIndex] = useState(0);
  const [view, setView] = useState(null);
  const [lastUpdatedDate, setLastUpdatedDate] = useState(null);
  const [tripDetails, setTripDetails] = useState(null);
  const isBaseScreen = useBreakpointValue({ base: true, md: false });

  const [isAddActivitiesOpen, setIsAddActivitiesOpen] = useState(false);
  const [isAddAccommodationOpen, setIsAddAccommodationOpen] = useState(false);
  const [isAddFlightOpen, setIsAddFlightOpen] = useState(false);
  const [isAddRVOpen, setIsAddRVOpen] = useState(false);
  const [isAddBusTicketOpen, setIsAddBusTicketOpen] = useState(false);
  const [isAddRentalCarOpen, setIsAddRentalCarOpen] = useState(false);
  const [isAddTrainTicketOpen, setIsAddTrainTicketOpen] = useState(false);
  const [isAddOtherTicketOpen, setIsAddOtherTicketOpen] = useState(false);
  const [isDownloadLoading, setIsDownloadLoading] = useState(false);
  const { currency, convertCurrency } = useCurrency();

  useEffect(() => {
    const fetchTripById = async () => {
      try {
        // console.log("tripId", tripId);
        const response = await axiosInstance.get(`/bucket-list/${tripId}`);
        const trip = response.data.data;

        if (trip) {
          setItinerary(trip.general_info); // Access the `itinerary` array directly
          setTripDetails(trip.trip_details);
          setLastUpdatedDate(trip.last_updated);
          setAllowedUsers(trip.allowed_users || []);

          setActivities(trip.viator_api_response[0]?.viator_response || []);
          setActivitiesLocation(trip.viator_api_response[0]?.location || "");
        } else {
          // console.log("No trip found with the given ID");
        }
      } catch (error) {
        console.error("Error fetching trip by ID:", error);
      }
    };

    fetchTripById();
  }, [tripId]);

  const selectedDestination =
    itinerary?.destinations?.[selectedCityIndex] || {};

  // City-specific data
  const cityHotels = selectedDestination.home_to_go_api_response || [];
  const cityFlights = itinerary?.flight_info || [];
  // console.log("bucketlist cityFlights",cityFlights);
  const cityRVs = selectedDestination.outdoorsy_api_response || [];
  const cityBusTickets = selectedDestination.bus_api_response || [];
  const cityRentalCars = selectedDestination.rental_car_api_response || [];
  const cityTrainTickets = selectedDestination.train_api_response || [];
  const cityOtherTickets =
    selectedDestination.other_transportation_api_response || [];
  const cityLocation =
    cityHotels[0]?.location || selectedDestination.city || "";
  const cityItinerary = selectedDestination.trip_plan?.itinerary || [];

  useEffect(() => {
    setSelectedDayIndex(0); // Optionally reset day tab as well
  }, [selectedCityIndex]);

  // --- Handlers for city-specific updates ---
  const handleHotelClick = (hotelId) => {
    // Remove hotel by id for the selected city
    const updatedHotels = cityHotels.filter((hotel) => hotel.id !== hotelId);
    updateCityData("home_to_go_api_response", updatedHotels);
  };

  const handleHotelUpdateClick = () => {
    setView("hotels");
  };

  const handleRVsClick = (rv) => {
    const updatedRVs = cityRVs.filter((r) => r !== rv);
    updateCityData("outdoorsy_api_response", updatedRVs);
  };

  const handleRVsUpdateClick = () => {
    setView("rvs");
  };

  const handleFlightClick = (flightId) => {
    const updatedFlights = cityFlights.filter((f) => f.id !== flightId);
    updateCityData("flight_info", updatedFlights);
  };

  const handleFlightUpdateClick = () => {
    setView("flights");
  };

  const handleBusTicketsUpdateClick = () => {
    setView("busTickets");
  };

  const handleBusTicketClick = (busTicketId) => {
    const updatedBusTickets = cityBusTickets.filter(
      (ticket) => ticket.id !== busTicketId
    );
    updateCityData("bus_api_response", updatedBusTickets);
  };

  const handleRentalCarsUpdateClick = () => {
    setView("rentalCars");
  };

  const handleRentalCarsClick = (rentalCarId) => {
    const updatedRentalCars = cityRentalCars.filter(
      (ticket) => ticket.id !== rentalCarId
    );
    updateCityData("rental_car_api_response", updatedRentalCars);
  };

  const handleTrainTicketsUpdateClick = () => {
    setView("trainTickets");
  };

  const handleTrainTicketClick = (trainTicketId) => {
    const updatedTrainTickets = cityTrainTickets.filter(
      (ticket) => ticket.id !== trainTicketId
    );
    updateCityData("train_api_response", updatedTrainTickets);
  };

  const handleOtherTicketsUpdateClick = () => {
    setView("otherTickets");
  };

  const handleOtherTicketClick = (otherTicketId) => {
    const updatedOtherTickets = cityOtherTickets.filter(
      (ticket) => ticket.id !== otherTicketId
    );
    updateCityData("other_transportation_api_response", updatedOtherTickets);
  };

  // Helper to update city-specific data in itinerary state
  const updateCityData = (key, newValue) => {
    if (!itinerary || !Array.isArray(itinerary.destinations)) return;
    const updatedDestinations = itinerary.destinations.map((dest, idx) =>
      idx === selectedCityIndex ? { ...dest, [key]: newValue } : dest
    );
    setItinerary({ ...itinerary, destinations: updatedDestinations });
  };

  const handleActivityClick = (latitude, longitude) => {
    setSelectedLocation({ latitude, longitude });
    setView("map"); // Switch view to map when a location is selected
  };

  const handleUpdateClick = () => {
    setView("activities"); // Switch view to activities when update button is clicked
  };

  const handleDayChange = (index) => {
    setSelectedDayIndex(index);
    if (isBaseScreen === false) {
      setView("activities");
    }
  };

  useEffect(() => {
    if (!view) {
      handleDayChange(0);
    }
  }, [isBaseScreen]);

  const handlePlacesUpdate = (payload) => {
    if (!itinerary || !Array.isArray(itinerary.destinations)) return;

    const nextDestinations = itinerary.destinations.map((dest, cIdx) => {
      if (cIdx !== selectedCityIndex) return dest; // other cities remain

      /* ------ Determine what we received ------ */
      // case 1: caller sent us a *full* trip_plan object { itinerary:[...] }
      if (payload && Array.isArray(payload.itinerary)) {
        return { ...dest, trip_plan: payload };
      }

      // case 2: caller sent us just the day's activity_detail_list array
      const todaysActs = Array.isArray(payload) ? payload : [];
      const oldTripPlan = dest.trip_plan || { itinerary: [] };

      const nextItineraryArr = oldTripPlan.itinerary.map((day, dIdx) =>
        dIdx === selectedDayIndex
          ? { ...day, activity_detail_list: todaysActs }
          : day
      );

      return {
        ...dest,
        trip_plan: { ...oldTripPlan, itinerary: nextItineraryArr },
      };
    });

    setItinerary({ ...itinerary, destinations: nextDestinations });
  };

  const handleAllActivitiesClick = (activity) => {
    const updatedActivities = activities.filter((a) => a !== activity);
    setActivities(updatedActivities);
  };

  const handleAllActivitiesUpdateClick = () => {
    setView("allActivities"); // Switch view to allActivities when the "Update Activities" button is clicked
  };

  const fetchCountryFlag = async (countryCode) => {
    const response = await fetch(
      `https://flagcdn.com/w320/${countryCode.toLowerCase()}.png`
    );
    const flagImageBytes = new Uint8Array(await response.arrayBuffer());
    return flagImageBytes;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const suffix =
      day === 1 || day === 21 || day === 31
        ? "st"
        : day === 2 || day === 22
        ? "nd"
        : day === 3 || day === 23
        ? "rd"
        : "th";

    return date
      .toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
      .replace(day.toString(), `${day}${suffix}`);
  };

  const currencyToSymbol = {
    EUR: "€",
    USD: "$",
    GBP: "£",
    CZK: "Kč",
    DKK: "kr",
    NOK: "kr",
    SEK: "kr",
    JPY: "¥",
    CNY: "¥",
    KRW: "₩",
    IDR: "Rp",
    MYR: "RM",
    PHP: "₱",
    VND: "₫",
    THB: "฿",
  };

  const handleDownloadPDF = async () => {
    setIsDownloadLoading(true);
    const pdfDoc = await PDFDocument.create();
    let page = pdfDoc.addPage([600, 800]);
    const { width, height } = page.getSize();

    const HEADER_BOTTOM_Y = height - 123; // Set after height is defined

    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
    const greenColor = rgb(0.1568, 0.3882, 0.1568);
    const greyColor = rgb(0.5, 0.5, 0.5);

    const MARGIN = 50;
    const USABLE = width - MARGIN * 2;
    const RIGHT_X = MARGIN + USABLE / 2;
    const DIVIDER_Y = height - 70;

    const FOOTER_HEIGHT = 55;
    const BOTTOM_MARGIN = 10;

    const logoBytes = await fetch(officialLogo).then((res) =>
      res.arrayBuffer()
    );
    const logoImage = await pdfDoc.embedPng(logoBytes);
    const logoDims = { width: 140, height: 25 };

    const qrBytes = await fetch(qrCode).then((res) => res.arrayBuffer());
    const qrImage = await pdfDoc.embedPng(qrBytes);
    const qrDims = { width: 150, height: 150 };

    const startX = 50;
    let y = HEADER_BOTTOM_Y; // Initial Y position
    let x = startX;
    let column = 0;
    const columnWidth = width - 100;
    const lineHeight = 12 * 1.2;

    const wrap = (txt, fnt, fs, max) => {
      const words = String(txt).split(" ");
      const lines = [];
      let cur = "";
      for (const w of words) {
        const test = cur ? `${cur} ${w}` : w;
        if (fnt.widthOfTextAtSize(test, fs) <= max) cur = test;
        else {
          if (cur) lines.push(cur);
          cur = w;
        }
      }
      if (cur) lines.push(cur);
      return lines;
    };

    const tripName = itinerary?.itinerary?.trip_name || "My Trip";
    let travelDates =
      (tripDetails?.departure_date
        ? formatDate(tripDetails.departure_date)
        : "N/A") +
      " - " +
      (tripDetails?.return_date ? formatDate(tripDetails.return_date) : "N/A");
    travelDates = await translateText(travelDates);

    const countryName = tripDetails?.country || "";
    const countryCode = countryName
      ? countries.getAlpha2Code(countryName, "en")
      : null;

    // Function to draw header
    const drawHeader = async () => {
      page.drawImage(logoImage, {
        x: 50,
        y: height - 50,
        width: logoDims.width,
        height: logoDims.height,
      });
      const travelText = await translateText("Travel ");
      page.drawText(travelText, {
        x: 50,
        y: height - 90,
        size: 24,
        font: boldFont,
        color: greenColor,
      });
      let prevWidth = font.widthOfTextAtSize(travelText, 24);
      const planText = await translateText("Plan");
      page.drawText(planText, {
        x: 60 + prevWidth,
        y: height - 90,
        size: 24,
        font: font,
        color: greenColor,
      });
      const translatedTripName = await translateText(tripName || "My Trip");
      wrap(translatedTripName, boldFont, 12, USABLE / 2 - 10).forEach((ln, i) =>
        page.drawText(ln, {
          x: RIGHT_X,
          y: height - 35 - i * 14,
          size: 12,
          font: boldFont,
        })
      );
      const tripNameWidth = boldFont.widthOfTextAtSize(translatedTripName, 16);
      if (countryCode) {
        const flagImageBytes = await fetchCountryFlag(countryCode);
        if (flagImageBytes) {
          const flagImage = await page.doc.embedPng(flagImageBytes);
          const flagWidth = 24;
          const flagHeight = 16;
          page.drawImage(flagImage, {
            x: 300 + tripNameWidth + 10,
            y: height - 35,
            width: flagWidth,
            height: flagHeight,
          });
        }
      }
      page.drawText(travelDates, {
        x: 300,
        y: DIVIDER_Y,
        size: 12,
        font: boldFont,
      });
      page.drawRectangle({
        x: 50,
        y: height - 110,
        width: columnWidth,
        height: 0.5,
        color: rgb(0.75, 0.75, 0.75),
      });
      y = HEADER_BOTTOM_Y; // Always reset y after drawing header
    };

    // Footer function
    const drawFooter = (isLastPage = false) => {
      if (isLastPage) {
        page.drawImage(qrImage, {
          x: width - qrDims.width - 50,
          y: 60,
          width: qrDims.width,
          height: qrDims.height,
        });
      }
      page.drawRectangle({
        x: 0,
        y: 0,
        width,
        height: FOOTER_HEIGHT,
        color: greenColor,
      });
      const msgTextWidth = font.widthOfTextAtSize(
        "Stay Wild, Explore More",
        16
      );
      page.drawText("Stay Wild, Explore More", {
        x: width / 2 - msgTextWidth / 2,
        y: 37,
        size: 16,
        font: boldFont,
        color: rgb(1, 1, 1),
      });
      const websiteTextWidth = font.widthOfTextAtSize("www.nomad360.io", 11);
      page.drawText("www.nomad360.io", {
        x: width / 2 - websiteTextWidth / 2,
        y: 20,
        size: 11,
        font: boldFont,
        color: rgb(1, 1, 1),
      });
      const copyright =
        "© 2025 NOMAD360. All Rights Reserved. Unauthorized distribution, duplication, or reproduction of this material is strictly prohibited.";
      const cpTextWidth = font.widthOfTextAtSize(copyright, 7);
      page.drawText(copyright, {
        x: width / 2 - cpTextWidth / 2,
        y: 5,
        size: 7,
        font: boldFont,
        color: rgb(1, 1, 1),
      });
    };

    // Add new page, draw footer for current page before switching
    const addNewPage = async () => {
      drawFooter(false);
      page = pdfDoc.addPage([600, 800]);
      await drawHeader();
      x = startX;
      column = 0;
    };

    const capitalizeFirstLetter = (text) => {
      return text.charAt(0).toUpperCase() + text.slice(1);
    };

    const splitTextIntoLines = (text, font, fontSize, maxWidth) => {
      const words = text.split(" ");
      const lines = [];
      let currentLine = "";
      words.forEach((word) => {
        const testLine = currentLine ? currentLine + " " + word : word;
        const testWidth = font.widthOfTextAtSize(testLine, fontSize);
        if (testWidth <= maxWidth) {
          currentLine = testLine;
        } else {
          lines.push(currentLine);
          currentLine = word;
        }
      });
      if (currentLine) {
        lines.push(currentLine);
      }
      return lines;
    };

    // Draw header only on first page
    await drawHeader();

    // Helper to check if there's enough space for content above the footer
    const ensureSpace = async (needed = 0) => {
      if (y < FOOTER_HEIGHT + BOTTOM_MARGIN + needed) {
        await addNewPage();
      }
    };

    // Draw "Transportation" header
    await ensureSpace(30);
    page.drawRectangle({
      x: startX,
      y: y - 15,
      width: columnWidth,
      height: 20,
      color: greenColor,
    });
    const translatedTransportationHeader = await translateText(
      "Transportation"
    );
    page.drawText(translatedTransportationHeader, {
      x: startX + 10,
      y: y - 10,
      size: 16,
      font: boldFont,
      color: rgb(1, 1, 1),
    });
    y -= 30;

    // Draw "Flights" subsection
    await ensureSpace(30);
    page.drawRectangle({
      x: startX + 10,
      y: y - 15,
      width: columnWidth - 10,
      height: 20,
      color: greyColor,
    });
    const translatedFlightsHeader = await translateText("Flights");
    page.drawText(translatedFlightsHeader, {
      x: startX + 20,
      y: y - 10,
      size: 14,
      font: boldFont,
      color: rgb(1, 1, 1),
    });
    y -= 30;

    const docCurrencySymbol = currencyToSymbol[currency];

    // Get all flights from all destinations
    const allFlights = itinerary?.flight_info || [];

    if (allFlights.length === 0) {
      await ensureSpace(lineHeight + 10);
      const translatedNoFlightsText = await translateText(
        "No flights selected yet. Scan the QR code now and find some inspiration!"
      );
      page.drawText(translatedNoFlightsText, {
        x: startX + 20,
        y: y,
        size: 12,
        font: boldFont,
        color: rgb(0, 0, 0),
      });
      y -= lineHeight + 10;
    } else {
      // Group flights by route
      for (const routeGroup of allFlights) {
        const route = routeGroup.route;
        const flights = routeGroup.flights || [];

        // Draw route header
        await ensureSpace(lineHeight + 10);
        page.drawText(route, {
          x: startX + 20,
          y: y,
          size: 12,
          font: boldFont,
          color: rgb(0, 0, 0),
        });
        y -= lineHeight + 10;

        // Draw flights under this route
        for (const flight of flights) {
          await ensureSpace(lineHeight * 3 + 15);
          const flightText = `${flight.airline} (${flight.origin} - ${flight.destination})`;
          page.drawText(flightText, {
            x: startX + 30,
            y: y,
            size: 12,
            font: boldFont,
            color: rgb(0, 0, 0),
          });
          y -= lineHeight;
          const departureText =
            (await translateText("Departure:")) +
            ` ${formatDate(flight.departure_at)} - ${flight.origin_airport}`;
          page.drawText(departureText, {
            x: startX + 30,
            y: y,
            size: 11,
            font: font,
            color: rgb(0, 0, 0),
          });
          y -= lineHeight;
          const returnText =
            (await translateText("Return:")) +
            ` ${formatDate(flight.return_at)} - ${flight.destination_airport}`;
          page.drawText(returnText, {
            x: startX + 30,
            y: y,
            size: 11,
            font: font,
            color: rgb(0, 0, 0),
          });
          y -= lineHeight;
          const priceText =
            (await translateText("Price:")) +
            ` ${docCurrencySymbol} ${convertCurrency(
              flight.price,
              "USD"
            )} (${currency})`;
          page.drawText(priceText, {
            x: startX + 30,
            y: y,
            size: 11,
            font: font,
            color: rgb(0, 0, 0),
          });
          y -= lineHeight + 15;
        }
      }
    }

    // Draw "RVs" subsection
    await ensureSpace(30);
    page.drawRectangle({
      x: startX + 10,
      y: y - 15,
      width: columnWidth - 10,
      height: 20,
      color: greyColor,
    });
    const translatedRVsHeader = await translateText("RVs");
    page.drawText(translatedRVsHeader, {
      x: startX + 20,
      y: y - 10,
      size: 14,
      font: boldFont,
      color: rgb(1, 1, 1),
    });
    y -= 30;

    if (cityRVs.length === 0) {
      await ensureSpace(lineHeight + 10);
      const translatedNoRVsText = await translateText(
        "No RVs selected yet. Scan the QR code now and find some inspiration!"
      );
      page.drawText(translatedNoRVsText, {
        x: startX + 20,
        y: y,
        size: 12,
        font: boldFont,
        color: rgb(0, 0, 0),
      });
      y -= lineHeight + 10;
    }
    for (const rv of cityRVs) {
      await ensureSpace(lineHeight * 2 + 15);
      const nameText = await translateText(`${rv.vehicle_name}`);
      page.drawText(nameText, {
        x: startX + 20,
        y: y,
        size: 11,
        font: font,
        color: rgb(0, 0, 0),
      });
      y -= lineHeight;
      const priceText =
        (await translateText("Price per day:")) +
        ` ${docCurrencySymbol} ${convertCurrency(
          rv.price_per_day,
          "USD"
        )} (${currency})`;
      page.drawText(priceText, {
        x: startX + 20,
        y: y,
        size: 11,
        font: font,
        color: rgb(0, 0, 0),
      });
      y -= lineHeight + 15;
    }

    // Draw "Accommodations" header
    await ensureSpace(30);
    page.drawRectangle({
      x: startX,
      y: y - 15,
      width: 500,
      height: 20,
      color: greenColor,
    });
    page.drawText(await translateText("Accommodations"), {
      x: startX + 10,
      y: y - 10,
      size: 14,
      font: boldFont,
      color: rgb(1, 1, 1),
    });
    y -= 30;

    if (cityHotels.length === 0) {
      await ensureSpace(lineHeight + 10);
      page.drawText(
        await translateText(
          "No accommodations selected yet. Scan the QR code now and find some inspiration!"
        ),
        { x: startX + 10, y: y, size: 12, font: boldFont, color: rgb(0, 0, 0) }
      );
      y -= lineHeight + 10;
    }
    for (const accommodation of cityHotels) {
      await ensureSpace(lineHeight * 2 + 10);
      const titleAccommodation = await translateText(accommodation.title);
      page.drawText(titleAccommodation, {
        x: startX + 10,
        y: y,
        size: 12,
        font: boldFont,
        color: rgb(0, 0, 0),
      });
      y -= lineHeight;
      const priceAccommodation = convertCurrency(
        accommodation.price.perNight,
        accommodation.price.currency
      );
      const priceText = `${await translateText(
        "Price per night:"
      )} ${docCurrencySymbol} ${priceAccommodation} (${currency})`;
      page.drawText(priceText, {
        x: startX + 10,
        y: y,
        size: 11,
        font: font,
        color: rgb(0, 0, 0),
      });
      y -= lineHeight + 10;
    }

    // Collect all days from all destinations sequentially
    const allDays = [];
    if (itinerary.destinations && Array.isArray(itinerary.destinations)) {
      itinerary.destinations.forEach((destination, cityIndex) => {
        const cityItinerary = destination.trip_plan?.itinerary || [];
        cityItinerary.forEach((day, dayIndex) => {
          allDays.push({
            ...day,
            cityIndex,
            cityName: destination.city || `City ${cityIndex + 1}`,
            originalDayIndex: dayIndex,
          });
        });
      });
    }

    // Keep days in order by city (no sorting by day number)

    // Group days by city
    const daysByCity = {};
    allDays.forEach((day) => {
      if (!daysByCity[day.cityName]) {
        daysByCity[day.cityName] = [];
      }
      daysByCity[day.cityName].push(day);
    });

    // Render each city with its days
    for (const [cityName, cityDays] of Object.entries(daysByCity)) {
      // Draw city header
      await ensureSpace(30);
      page.drawRectangle({
        x: startX,
        y: y - 15,
        width: columnWidth,
        height: 20,
        color: greenColor,
      });
      const translatedCityName = await translateText(cityName);
      page.drawText(translatedCityName, {
        x: 60,
        y: y - 10,
        size: 16,
        font: boldFont,
        color: rgb(1, 1, 1),
      });
      y -= 30;

      // Draw days for this city
      for (const day of cityDays) {
        await ensureSpace(30);
        const dayTitle = await translateText(
          `Day ${day.day}: ${day.current_day_name}`
        );
        page.drawRectangle({
          x: startX + 10,
          y: y - 15,
          width: columnWidth - 10,
          height: 20,
          color: greyColor,
        });
        page.drawText(dayTitle, {
          x: 70,
          y: y - 10,
          size: 14,
          font: boldFont,
          color: rgb(1, 1, 1),
        });
        y -= 30;

        for (const v of Object.entries(day.plan)) {
          await ensureSpace(30);
          const [section, content] = v;
          const capitalizedSection = await translateText(
            capitalizeFirstLetter(section)
          );
          page.drawText(capitalizedSection, {
            x: 70,
            y: y - 10,
            size: 12,
            font: boldFont,
            color: rgb(0, 0, 0),
          });
          y -= 30;

          const lines = splitTextIntoLines(
            await translateText(content),
            font,
            12,
            columnWidth - 20
          );
          for (const line of lines) {
            await ensureSpace(lineHeight);
            page.drawText(line, {
              x: 70,
              y: y,
              size: 12,
              font: font,
              color: rgb(0, 0, 0),
            });
            y -= lineHeight;
          }
          y -= 10;
        }
        if (day.activity_name_list && day.activity_name_list.length > 0) {
          await ensureSpace(30);
          page.drawText(await translateText("Activities"), {
            x: 70,
            y: y - 10,
            size: 12,
            font: boldFont,
            color: rgb(0, 0, 0),
          });
          y -= 30;
          for (const activity of day.activity_name_list) {
            await ensureSpace(lineHeight);
            page.drawText(`• ${await translateText(activity)}`, {
              x: 70,
              y: y,
              size: 12,
              font: font,
              color: rgb(0, 0, 0),
            });
            y -= lineHeight;
          }
        }
        y -= 10;
      }
    }

    // Draw footer for the last page (with QR code)
    drawFooter(true);

    // Save the PDF
    const pdfBytes = await pdfDoc.save();
    const blob = new Blob([pdfBytes], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.style.display = "none";
    a.href = url;
    a.download = await translateText(`${tripName || "My Trip"}_Trip_Plan.pdf`);
    document.body.appendChild(a);
    a.click();
    URL.revokeObjectURL(url);
    document.body.removeChild(a);
    setIsDownloadLoading(false);
  };

  // const handleSuggestionClick = (message) => {
  //   localStorage.removeItem("conversation_id");
  //   localStorage.setItem("conversation_id", tripId);
  //   history.push(
  //     `/ai-travel-planner/chat?message=${encodeURIComponent(message)}`
  //   );
  // };

  const handleSuggestionClick = (
    message,
    conversationType = "general_conversation"
  ) => {
    localStorage.removeItem("conversation_id");
    localStorage.setItem("activeTab", JSON.stringify(conversationType));
    localStorage.setItem("conversation_id", tripId);
    history.push(
      `/ai-travel-planner/chat?message=${encodeURIComponent(
        message
      )}&conversation_type=${conversationType}`
    );
  };
  return (
    <Box
      display="flex"
      bg={colorMode === "dark" ? "gray.800" : "blackAlpha.50"}
      w={{ base: "100%", md: "83vw" }}
      h="100vh"
      overflowY="hidden"
    >
      {(!isBaseScreen || !view) && (
        <Box
          flex="5"
          w="50%"
          display="flex"
          flexDirection="column"
          ml={0}
          bg={colorMode === "dark" ? "gray.700" : "white"}
          overflowY="hidden"
        >
          <ItineraryHeader
            tripId={tripId}
            downloadPDF={handleDownloadPDF}
            allowedUsers={allowedUsers}
            setAllowedUsers={setAllowedUsers}
            isLoading={isDownloadLoading}
          />

          <Box
            flex="9"
            display="flex"
            alignItems="flex-start"
            justifyContent="center"
            maxH="90vh"
            bg={colorMode === "dark" ? "gray.700" : "white"}
          >
            {/* <ItineraryDetails
              item={itinerary}
              activities={activities} // Pass for AllActivitiesList
              hotels={hotels}
              flights={flights}
              RVs={rvs}
              onActivityClick={handleActivityClick}
              onUpdateClick={handleUpdateClick}
              onDayChange={handleDayChange}
              selectedDayIndex={selectedDayIndex}
              handleHotelUpdateClick={handleHotelUpdateClick}
              handleRVsUpdateClick={handleRVsUpdateClick}
              handleFlightUpdateClick={handleFlightUpdateClick}
              handleAllActivitiesUpdateClick={handleAllActivitiesUpdateClick}
              lastUpdatedDate={lastUpdatedDate}
              tripDetails={tripDetails}
              setTripDetails={setTripDetails}
              tripId={tripId}
              isBaseScreen={isBaseScreen}
              exploreSelections={exploreSelections}
              setExploreSelections={setExploreSelections}
              accommodationSelections={accommodationSelections}
              setAccommodationSelections={setAccommodationSelections}
              transportSelections={transportSelections}
              setTransportSelections={setTransportSelections}
              handleSuggestionClick={handleSuggestionClick}
              setIsAddActivitiesOpen={setIsAddActivitiesOpen}
              setIsAddAccommodationOpen={setIsAddAccommodationOpen}
              setIsAddFlightOpen={setIsAddFlightOpen}
              isAddRVOpen={isAddRVOpen}
              setIsAddRVOpen={setIsAddRVOpen}
            /> */}

            <ManageItinerary
              generalInfo={itinerary}
              tripId={tripId}
              lastUpdated={lastUpdatedDate}
              onUpdateClick={handleUpdateClick}
              selectedDayIndex={selectedDayIndex}
              setSelectedDayIndex={setSelectedDayIndex}
              selectedCityIndex={selectedCityIndex}
              setSelectedCityIndex={setSelectedCityIndex}
              tripDetails={tripDetails}
              setTripDetails={setTripDetails}
              handleHotelUpdateClick={handleHotelUpdateClick}
              handleRVsUpdateClick={handleRVsUpdateClick}
              handleFlightUpdateClick={handleFlightUpdateClick}
              handleAllActivitiesUpdateClick={handleAllActivitiesUpdateClick}
              handleSuggestionClick={handleSuggestionClick}
              setIsAddActivitiesOpen={setIsAddActivitiesOpen}
              setIsAddAccommodationOpen={setIsAddAccommodationOpen}
              setIsAddFlightOpen={setIsAddFlightOpen}
              setIsAddRVOpen={setIsAddRVOpen}
              setIsAddBusTicketOpen={setIsAddBusTicketOpen}
              handleDayChange={handleDayChange}
              handleBusTicketsUpdateClick={handleBusTicketsUpdateClick}
              handleRentalCarsUpdateClick={handleRentalCarsUpdateClick}
              setIsAddRentalCarOpen={setIsAddRentalCarOpen}
              handleTrainTicketsUpdateClick={handleTrainTicketsUpdateClick}
              setIsAddTrainTicketOpen={setIsAddTrainTicketOpen}
              handleOtherTicketsUpdateClick={handleOtherTicketsUpdateClick}
              setIsAddOtherTicketOpen={setIsAddOtherTicketOpen}
            />
          </Box>
        </Box>
      )}
      {(!isBaseScreen || view) && (
        <Box
          flex="5"
          w="50%"
          h="100vh"
          backgroundPosition="center"
          display="flex"
          flexDirection="column"
          borderLeftRadius="xl"
          boxLeftShadow="xl"
        >
          {view === "map" && (
            <GoogleMapsLink
              latitude={selectedLocation.latitude}
              longitude={selectedLocation.longitude}
              setView={setView}
            />
          )}
          {view === "activities" && (
            <ActivitiesList
              key={`activities-${selectedCityIndex}-${selectedDayIndex}`}
              places={selectedDestination.trip_plan}
              setPlaces={handlePlacesUpdate}
              onActivityClick={handleActivityClick}
              tripId={tripId}
              selectedDayIndex={selectedDayIndex}
              itinerary={itinerary}
              setItinerary={setItinerary}
              setView={setView}
              handleSuggestionClick={handleSuggestionClick}
              isAddActivitiesOpen={isAddActivitiesOpen}
              setIsAddActivitiesOpen={setIsAddActivitiesOpen}
              city={selectedDestination.city}
            />
          )}

          {view === "hotels" && (
            <HotelsList
              key={`hotels-${selectedCityIndex}`}
              hotels={cityHotels}
              setHotels={(newHotels) =>
                updateCityData("home_to_go_api_response", newHotels)
              }
              onHotelClick={handleHotelClick}
              tripId={tripId} // Pass the tripId to HotelsList
              location={cityLocation}
              setView={setView}
              handleSuggestionClick={handleSuggestionClick}
              isAddAccommodationOpen={isAddAccommodationOpen}
              setIsAddAccommodationOpen={setIsAddAccommodationOpen}
              city={selectedDestination.city}
            />
          )}
          {view === "flights" && (
            <FlightsList
              key={`flights-${selectedCityIndex}`}
              flights={cityFlights}
              setFlights={(newFlights) =>
                updateCityData("flight_info", newFlights)
              }
              onFlightClick={handleFlightClick}
              tripId={tripId}
              setView={setView}
              handleSuggestionClick={handleSuggestionClick}
              isAddFlightOpen={isAddFlightOpen}
              setIsAddFlightOpen={setIsAddFlightOpen}
              city={selectedDestination.city}
            />
          )}
          {view === "allActivities" && (
            <AllActivitiesList
              activities={activities} // Use activities state for AllActivitiesList
              setActivities={setActivities}
              onActivityClick={handleAllActivitiesClick}
              tripId={tripId}
              location={activitiesLocation}
              setView={setView}
            />
          )}

          {view === "rvs" && (
            <RVsList
              key={`rvs-${selectedCityIndex}`}
              rvs={cityRVs}
              setRVs={(newRVs) =>
                updateCityData("outdoorsy_api_response", newRVs)
              }
              onRVsClick={handleRVsClick}
              tripId={tripId} // Pass the tripId to HotelsList
              location={cityLocation}
              setView={setView}
              handleSuggestionClick={handleSuggestionClick}
              isAddRVOpen={isAddRVOpen}
              setIsAddRVOpen={setIsAddRVOpen}
              city={selectedDestination.city}
            />
          )}

          {view === "busTickets" && (
            <BusTicketList
              key={`busTickets-${selectedCityIndex}`}
              busTickets={cityBusTickets}
              setBusTickets={(newBusTickets) =>
                updateCityData("bus_api_response", newBusTickets)
              }
              onBusTicketClick={handleBusTicketClick}
              tripId={tripId}
              setView={setView}
              isAddBusTicketOpen={isAddBusTicketOpen}
              setIsAddBusTicketOpen={setIsAddBusTicketOpen}
              city={selectedDestination.city}
            />
          )}

          {view === "rentalCars" && (
            <RentalCarList
              key={`rentalCars-${selectedCityIndex}`}
              rentalCars={cityRentalCars}
              setRentalCars={(newRentalCars) =>
                updateCityData("rental_car_api_response", newRentalCars)
              }
              onRentalCarClick={handleRentalCarsClick}
              tripId={tripId}
              setView={setView}
              isAddRentalCarOpen={isAddRentalCarOpen}
              setIsAddRentalCarOpen={setIsAddRentalCarOpen}
              city={selectedDestination.city}
            />
          )}

          {view === "trainTickets" && (
            <TrainTicketList
              key={`trainTickets-${selectedCityIndex}`}
              trainTickets={cityTrainTickets}
              setTrainTickets={(newTrainTickets) =>
                updateCityData("train_api_response", newTrainTickets)
              }
              onTrainTicketClick={handleTrainTicketClick}
              tripId={tripId}
              setView={setView}
              isAddTrainTicketOpen={isAddTrainTicketOpen}
              setIsAddTrainTicketOpen={setIsAddTrainTicketOpen}
              city={selectedDestination.city}
            />
          )}
          {view === "otherTickets" && (
            <OtherList
              key={`otherTickets-${selectedCityIndex}`}
              otherTickets={cityOtherTickets}
              setOtherTickets={(newOtherTickets) =>
                updateCityData(
                  "other_transportation_api_response",
                  newOtherTickets
                )
              }
              tripId={tripId}
              setView={setView}
              isAddOtherTicketOpen={isAddOtherTicketOpen}
              setIsAddOtherTicketOpen={setIsAddOtherTicketOpen}
              city={selectedDestination.city}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

export default ItineraryDetail;
